import type { SxProps, Theme } from '@mui/material';
import type { AddressAutocompleteOption } from '@/utils/types/addressAutocompleteOption.interface';

import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Grid, Skeleton, Typography } from '@mui/material';

import { Autocomplete } from '@theguarantors/tg-package-material-ui';

import { useAddresses } from './useAddresses';

export type AddressesAutocompleteSkeletonProps = {
  enabled?: boolean;
  height?: number;
};

const DEFAULT_SKELETON: Required<AddressesAutocompleteSkeletonProps> = {
  enabled: true,
  height: 56,
};

export type AddressesAutocompleteProps = {
  value: number;
  /**
   * Address already chosen, including one that is not in the loaded page.
   * The widget matches `value` by building id, so a saved invite whose building
   * is outside that page would otherwise render blank.
   */
  selectedOption?: AddressAutocompleteOption | null;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  label?: string;
  name?: string;
  color?: 'primary' | 'secondary';
  size?: 'small' | 'medium';
  inputSx?: SxProps<Theme>;
  skeleton?: AddressesAutocompleteSkeletonProps;
  onBlur?: () => void;
  onChange: (addressOption: AddressAutocompleteOption | null) => void;
  'data-testid'?: string;
};

const filterOptions = (options: AddressAutocompleteOption[]) => options;

const findAddressByBuildingId = (
  addresses: AddressAutocompleteOption[] | undefined,
  buildingId: number,
): AddressAutocompleteOption | undefined => {
  if (!buildingId) {
    return undefined;
  }
  return addresses?.find((address) => address.monolith_building_id === buildingId);
};

const savedAddressOption = (
  selectedOption: AddressAutocompleteOption | null | undefined,
  buildingId: number,
): AddressAutocompleteOption | undefined => {
  if (!selectedOption) {
    return undefined;
  }
  if (!selectedOption.name && !selectedOption.street) {
    return undefined;
  }
  if (buildingId && selectedOption.monolith_building_id !== buildingId) {
    return undefined;
  }
  return selectedOption;
};

const resolveSelectedAddress = (
  addresses: AddressAutocompleteOption[] | undefined,
  buildingId: number,
  selectedOption: AddressAutocompleteOption | null | undefined,
  lastMatched: AddressAutocompleteOption | undefined,
): AddressAutocompleteOption | undefined =>
  findAddressByBuildingId(addresses, buildingId) ??
  savedAddressOption(selectedOption, buildingId) ??
  (buildingId ? lastMatched : undefined);

const addressOptions = (
  addresses: AddressAutocompleteOption[] | undefined,
  selectedValue: AddressAutocompleteOption | undefined,
  isSearchLoading: boolean,
): AddressAutocompleteOption[] => {
  if (isSearchLoading) {
    return [];
  }
  const list = addresses ?? [];
  const alreadyListed = list.some(
    (address) => address.monolith_building_id === selectedValue?.monolith_building_id,
  );
  if (selectedValue && !alreadyListed) {
    return [selectedValue, ...list];
  }
  return list;
};

export const AddressesAutocomplete = forwardRef(
  (
    {
      label,
      error,
      required,
      helperText,
      onBlur,
      name,
      disabled,
      value,
      selectedOption,
      onChange,
      color = 'primary',
      size,
      skeleton,
      inputSx,
      'data-testid': dataTestId = 'property-chooser',
    }: AddressesAutocompleteProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const skeletonConfig = { ...DEFAULT_SKELETON, ...skeleton };
    const { addresses, isLoading, isSearchLoading, onSearchChange } = useAddresses();

    const createOptionsLabel = useCallback((option: AddressAutocompleteOption) => {
      const isDuplicatePropertyName =
        option.property_name?.trim().toLowerCase() === option.name.trim().toLowerCase();

      const secondaryText = isDuplicatePropertyName ? '' : option.property_name;

      return (
        <Grid
          container={true}
          alignItems="center"
          data-testid={`addresses-autocomplete-option-${option.id}`}
        >
          <Grid>
            <Box component={LocationOnIcon} sx={{ color: 'text.secondary', mr: 2 }} />
          </Grid>
          <Grid size="grow">
            <span
              style={{
                fontWeight: 700,
              }}
            >
              {option.name}
            </span>
            {secondaryText ? (
              <Typography variant="body2" color="text.secondary">
                {secondaryText}
              </Typography>
            ) : null}
          </Grid>
        </Grid>
      );
    }, []);

    const lastMatchedAddressOption = useRef<AddressAutocompleteOption | undefined>(undefined);

    const selectedValue = useMemo(
      () =>
        resolveSelectedAddress(addresses, value, selectedOption, lastMatchedAddressOption.current),
      [addresses, selectedOption, value],
    );

    useEffect(() => {
      const found = addresses?.find((addr) => addr.monolith_building_id === value);
      if (found) {
        lastMatchedAddressOption.current = found;
      } else if (!value) {
        lastMatchedAddressOption.current = undefined;
      }
    }, [addresses, value]);

    const handleInputChange = useCallback(
      (_event: React.SyntheticEvent, inputValue: string, reason: string) => {
        if (reason === 'input') {
          onSearchChange(inputValue);
        } else if (reason === 'clear') {
          onSearchChange('');
        }
      },
      [onSearchChange],
    );

    if (isLoading && skeletonConfig.enabled) {
      return (
        <Skeleton
          data-testid="addresses-autocomplete-loading-skeleton"
          variant="rounded"
          height={skeletonConfig.height}
        />
      );
    }

    return (
      <Autocomplete<AddressAutocompleteOption, false, false, false>
        clearOnBlur={true}
        size={size}
        id={'property-chooser'}
        data-testid={dataTestId}
        options={addressOptions(addresses, selectedValue, isSearchLoading)}
        value={selectedValue}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value: AddressAutocompleteOption | null): void => onChange(value)}
        onInputChange={handleInputChange}
        disabled={disabled}
        loading={isSearchLoading}
        loadingText={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
            <Skeleton variant="rounded" height={40} />
          </Box>
        }
        getOptionLabel={(option: AddressAutocompleteOption) => {
          const showProperty =
            option.property_name &&
            option.property_name.trim().toLowerCase() !== option.name.trim().toLowerCase();
          return showProperty ? `${option.name} - ${option.property_name}` : option.name;
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            {createOptionsLabel(option)}
          </li>
        )}
        textFieldAdditionalProps={{
          label,
          error,
          required,
          helperText,
          sx: inputSx,
          onBlur,
          name,
          value,
          ref,
          color,
        }}
      />
    );
  },
);

export const formatAddressListValue = (option: AddressAutocompleteOption) =>
  [option.name, option.city, option.state, option.zip_code].filter(Boolean).join(' ');

export { useAddresses } from './useAddresses';
